import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, AlertTitle, Button } from '@mui/material';
import DocumentViewer from '@/components/viewers/DocumentViewer';
import SimpleLayout from '@/components/SimpleLayout';

/**
 * Type definition for document viewer state
 * @typedef {Object} DocumentViewerState
 * @property {string} url - The URL of the document file
 * @property {string} name - The name of the document file
 */
type DocumentViewerState = {
  url: string;
  name: string;
};

/**
 * Type definition for error state
 * @typedef {Object} ErrorState
 * @property {string} message - Error message to display
 */
type ErrorState = {
  message: string;
};

/**
 * Document Viewer Page Component
 * 
 * A full-page component for displaying document files (DOC, DOCX) in a user-friendly interface.
 * Retrieves file information from session storage and renders the appropriate viewer.
 * 
 * @component
 * @returns {JSX.Element} The document viewer page component
 * 
 * @example
 * // To use this component, set the file data in session storage:
 * sessionStorage.setItem('documentViewer', JSON.stringify({
 *   url: 'https://example.com/document.docx',
 *   name: 'Report.docx'
 * }));
 * 
 * @description
 * This component provides:
 * - Full-page document viewing experience
 * - Support for various document formats (DOC, DOCX)
 * - Loading states while the file is being loaded
 * - Error handling for missing or invalid file data
 * - Clean navigation with a back button
 */
const DocumentViewerPage: React.FC = () => {
  /**
   * State for managing file data and UI state
   * @type {Object}
   * @property {string} fileUrl - URL of the document file to display
   * @property {string} fileName - Name of the document file
   * @property {ErrorState | null} error - Error state for handling display errors
   */
  const [fileState, setFileState] = useState<DocumentViewerState>({ url: '', name: '' });
  const [error, setError] = useState<ErrorState | null>(null);
  
  /**
   * Router navigation hook
   */
  const navigate = useNavigate();

  /**
   * Effect hook to load file data from session storage on component mount
   * @effect
   * @dependencies []
   * @description
   * - Retrieves file data from session storage
   * - Validates the data and updates component state
   * - Sets error state if data is missing or invalid
   */
  useEffect(() => {
    // Get file info from session storage
    const fileInfo = sessionStorage.getItem('documentViewer');
    
    if (!fileInfo) {
      setError({ message: 'No file information found. Please go back and try again.' });
      return;
    }

    try {
      const parsedData = JSON.parse(fileInfo) as DocumentViewerState;
      if (!parsedData.url || !parsedData.name) {
        throw new Error('Invalid file information');
      }
      setFileState({
        url: parsedData.url,
        name: parsedData.name
      });
    } catch (err) {
      console.error('Error parsing file info:', err);
      setError({ 
        message: 'Invalid file information. Please go back and try again.' 
      });
    }
  }, []);

  /**
   * Handles the close action for the document viewer
   * @function handleClose
   * @returns {void}
   * @description
   * - Clears the file data from session storage
   * - Navigates back to the previous page
   */
  const handleClose = (): void => {
    // Clear the session storage and navigate back
    sessionStorage.removeItem('documentViewer');
    navigate(-1); // Go back to previous page
  };

  // Render error state if there's an error
  if (error) {
    return (
      <SimpleLayout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error.message}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
        </Box>
      </SimpleLayout>
    );
  }

  // Show loading state while file data is being loaded
  if (!fileState.url || !fileState.name) {
    return (
      <SimpleLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </SimpleLayout>
    );
  }

  // Render the DocumentViewer component with the loaded file data
  return (
    <SimpleLayout>
      <DocumentViewer 
        fileUrl={fileState.url} 
        fileName={fileState.name} 
        onClose={handleClose} 
      />
    </SimpleLayout>
  );
};

export default DocumentViewerPage;
